import React from 'react';
import { useDocument } from '@nandorojo/swr-firestore';
import { useRecoilValue } from 'recoil';
import { CollectionPath } from '@typings/collections.enum';
import { IQuest, IStatReward, RewardType } from '@typings/quest/questTypes';
import { IstoreItem } from '@typings/store/storeItem.interface';
import { IstatusItem } from '@typings/status/statusItem.interface';
import { ImemberTitle } from '@typings/setting/memberTitle.interface';
import { defaultCurrencyState } from '@atom/setting/homeSettingState';
import ItemImgCard from '@components/dataDisplay/Store/ItemImgCard';
import styled from 'styled-components';
import {
  Section,
  SectionTitle,
  RewardsList,
  RewardItem,
  RewardIcon,
  RewardInfo,
  RewardName,
  RewardAmount,
} from './mission.styles';

const RandomBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 16px;
  background: linear-gradient(135deg, var(--primaryColor) 0%, #a855f7 100%);
  color: white;
  margin-left: 8px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom:6px;
`;

const BranchBlock = styled.div<{ $type: 'success' | 'failure' }>`
  margin-bottom: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${({ $type }) =>
    $type === 'success'
      ? 'rgba(16, 185, 129, 0.3)'
      : 'rgba(239, 68, 68, 0.2)'};
  background: ${({ $type }) =>
    $type === 'success'
      ? 'rgba(16, 185, 129, 0.05)'
      : 'rgba(239, 68, 68, 0.04)'};
`;

const BranchLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: var(--subTextColor);
  margin-bottom: 8px;
`;

// 단일 아이템 보상 렌더링 컴포넌트
function SingleItemReward({
  itemId,
  amount,
}: {
  itemId: string;
  amount: number;
}) {
  const { data: itemData } = useDocument<IstoreItem>(
    itemId ? `${CollectionPath.STORE}/${itemId}` : null,
    { listen: true },
  );

  return (
    <RewardItem>
      <ItemImgCard data={itemData} style={{ width: '32px', height: '32px' }} />
      <RewardInfo>
        <RewardName>{itemData?.title || '아이템'}</RewardName>
        <RewardAmount>× {amount || 1}</RewardAmount>
      </RewardInfo>
    </RewardItem>
  );
}

// 단일 스탯 보상 렌더링 컴포넌트
function SingleStatReward({ statReward }: { statReward: IStatReward }) {
  const { data: statData } = useDocument<IstatusItem>(
    statReward.statId ? `${CollectionPath.STATUS}/${statReward.statId}` : null,
    { listen: true },
  );

  const isPositive = statReward.amount >= 0;

  return (
    <RewardItem>
      <RewardIcon
        style={{
          backgroundColor: isPositive
            ? 'var(--primaryColor)'
            : 'var(--dangerColor)',
        }}
      >
        {isPositive ? '📈' : '📉'}
      </RewardIcon>
      <RewardInfo>
        <RewardName>{statData?.value || '스탯'}</RewardName>
        <RewardAmount>
          {isPositive ? '+' : ''}
          {statReward.amount}
        </RewardAmount>
      </RewardInfo>
    </RewardItem>
  );
}

// 보상 행 컴포넌트: 재화/아이템/스탯 분기 렌더링 (RewardReceiptModal에서도 사용)
export function QuestRewardItem({ reward }: { reward: any }) {
  const isItem = reward?.type === RewardType.ITEM;
  const isCurrency = reward?.type === RewardType.CURRENCY;
  const isTitle = reward?.type === RewardType.TITLE;
  const isStat = reward?.type === RewardType.STAT;

  // 아이템이면 store에서 데이터 조회 (하위호환: itemIds 우선, 없으면 itemId 사용)
  const itemIds: string[] =
    (reward as any)?.itemIds ||
    ((reward as any)?.itemId ? [(reward as any).itemId] : []);
  const itemId = itemIds[0] || '';
  const rewardItemData = (reward as any)?.itemData as Partial<IstoreItem> | undefined;

  // 단일 아이템용 (다중이 아닌 경우)
  const { data: itemData } = useDocument<IstoreItem>(
    isItem && itemIds.length === 1 && itemId
      ? `${CollectionPath.STORE}/${itemId}`
      : null,
    { listen: true },
  );

  // 타이틀 데이터 조회 (titleId 우선, 없으면 itemId 사용 - 하위호환)
  const titleDocId = reward?.titleId || reward?.itemId;
  const { data: titleData } = useDocument<ImemberTitle>(
    isTitle && titleDocId
      ? `${CollectionPath.MEMBERTITLE}/${titleDocId}`
      : null,
    { listen: true },
  );

  const defaultCurrency = useRecoilValue(defaultCurrencyState);

  if (isItem) {
    // 다중 아이템인 경우 각각 렌더링
    if (itemIds.length > 1) {
      return (
        <>
          {itemIds.map((id) => (
            <SingleItemReward
              key={id}
              itemId={id}
              amount={reward?.amount || 1}
            />
          ))}
        </>
      );
    }
    // 단일 아이템
    const resolvedItemData = itemData || rewardItemData;
    return (
      <RewardItem>
        <ItemImgCard
          data={resolvedItemData}
          style={{ width: '32px', height: '32px' }}
        />
        <RewardInfo>
          <RewardName>
            {resolvedItemData?.title || reward?.name || '아이템'}
          </RewardName>
          <RewardAmount>× {reward?.amount || 1}</RewardAmount>
        </RewardInfo>
      </RewardItem>
    );
  }

  if (isCurrency) {
    return (
      <RewardItem>
        <RewardIcon>
          <img src={defaultCurrency.moneyImgURL} alt="" />
        </RewardIcon>
        <RewardInfo>
          <RewardName>{reward?.name || '재화'}</RewardName>
          <RewardAmount>
            × {reward?.amount || 0} {defaultCurrency.moneyUnit}
          </RewardAmount>
        </RewardInfo>
      </RewardItem>
    );
  }

  if (isTitle) {
    const isTextType = titleData?.titleType === 'text';
    return (
      <RewardItem>
        {isTextType ? (
          <RewardIcon
            style={{
              fontSize: '0.7rem',
              padding: '4px 8px',
              color: titleData?.textColor || '#ffffff',
              backgroundColor: titleData?.bgColor || '#6366f1',
              borderRadius: '4px',
              width: 'auto',
              height: 'auto',
            }}
          >
            {titleData?.displayText || titleData?.title || '🏷️'}
          </RewardIcon>
        ) : (
          <RewardIcon>
            {titleData?.imgURL ? (
              <img src={titleData.imgURL} alt={titleData?.title || '타이틀'} />
            ) : (
              '🏷️'
            )}
          </RewardIcon>
        )}
        <RewardInfo>
          <RewardName>
            {titleData?.title || reward?.name || '타이틀'}
          </RewardName>
        </RewardInfo>
      </RewardItem>
    );
  }

  // 스탯 보상 렌더링: 각 스탯별로 개별 표시
  if (isStat) {
    const statRewards: IStatReward[] = reward?.statRewards || [];
    if (statRewards.length === 0) {
      return (
        <RewardItem>
          <RewardIcon>📊</RewardIcon>
          <RewardInfo>
            <RewardName>스탯 보상</RewardName>
            <RewardAmount>설정된 스탯 없음</RewardAmount>
          </RewardInfo>
        </RewardItem>
      );
    }
    return (
      <>
        {statRewards.map((statReward, idx) => (
          <SingleStatReward key={idx} statReward={statReward} />
        ))}
      </>
    );
  }

  // 탐색 포인트 보상
  if (reward?.type === RewardType.EXPLORATION_POINT) {
    const isPositive = (reward?.amount ?? 0) >= 0;
    return (
      <RewardItem>
        <RewardIcon
          style={{
            backgroundColor: isPositive
              ? 'var(--primaryColor)'
              : 'var(--dangerColor, #ef4444)',
          }}
        >
          🧭
        </RewardIcon>
        <RewardInfo>
          <RewardName>탐색 포인트</RewardName>
          <RewardAmount>
            {isPositive ? '+' : ''}
            {reward?.amount ?? 0}
          </RewardAmount>
        </RewardInfo>
      </RewardItem>
    );
  }

  // 기본(경험치/칭호 등) 렌더링: 기존 구조 유지
  return (
    <RewardItem>
      <RewardIcon>{reward?.icon || '🎁'}</RewardIcon>
      <RewardInfo>
        <RewardName>{reward?.name || '경험치'}</RewardName>
        {reward?.amount != null && (
          <RewardAmount>× {reward?.amount}</RewardAmount>
        )}
      </RewardInfo>
    </RewardItem>
  );
}

type Props = {
  missionData: IQuest;
};

function MissionRewards({ missionData }: Props) {
  const probability = (missionData as any).probability;
  const branchRewards = (missionData as any).branchRewards;
  const isProbabilityEnabled = probability?.enabled === true;

  // 확률 시스템 ON + 분기 보상 있을 때: 성공/실패 보상 분리 표시
  if (isProbabilityEnabled && branchRewards) {
    const successRewards: any[] = branchRewards.successRewards ?? [];
    const failureRewards: any[] = branchRewards.failureRewards ?? [];
    const hasAnyReward = successRewards.length > 0 || failureRewards.length > 0;
    if (!hasAnyReward) return null;

    return (
      <Section>
        <SectionTitle style={{ marginBottom: 8 }}>보상</SectionTitle>

        {successRewards.length > 0 && (
          <BranchBlock $type="success">
            <BranchLabel>성공 보상</BranchLabel>
            <RewardsList>
              {successRewards.map((reward: any, index: number) => (
                <QuestRewardItem key={index} reward={reward} />
              ))}
            </RewardsList>
          </BranchBlock>
        )}

        {failureRewards.length > 0 && (
          <BranchBlock $type="failure">
            <BranchLabel>실패 보상</BranchLabel>
            <RewardsList>
              {failureRewards.map((reward: any, index: number) => (
                <QuestRewardItem key={index} reward={reward} />
              ))}
            </RewardsList>
          </BranchBlock>
        )}
      </Section>
    );
  }

  // 기존 동작: rewards 배열 사용 (확률 OFF 또는 분기 보상 없음)
  if (!missionData.rewards || missionData.rewards.length === 0) {
    return null;
  }

  const isRandomReward = missionData.isRandomReward ?? false;

  return (
    <Section>
      <TitleRow>
        <SectionTitle style={{ marginBottom: 0 }}>보상</SectionTitle>
        {isRandomReward && (
          <RandomBadge>
            랜덤 지급
          </RandomBadge>
        )}
      </TitleRow>
      <RewardsList>
        {missionData.rewards.map((reward, index) => (
          <QuestRewardItem key={index} reward={reward} />
        ))}
      </RewardsList>
    </Section>
  );
}

export default MissionRewards;
